import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { IconArrowUpRight, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BoatCreationModal } from '../components/boats/BoatCreationModal';
import { PlanSelectionModal } from '../components/subscription/PlanSelectionModal';
import { WeatherWidget } from '../components/WeatherWidget';
import { ReactComponent as AnalyticsSearchIcon } from '../react-icons/analyticsSearch.svg';
import { ReactComponent as ChartRaceDataIcon } from '../react-icons/chartRaceData.svg';
import { ReactComponent as ClipboardReportsIcon } from '../react-icons/clipboardReports.svg';
import { createMyBoat, fetchMyBoatSetup, type BoatCreationSetup } from '../services/boats';
import { fetchHomeEvents, type HomeEventSummary } from '../services/home';
import {
  PlanPromptReason,
  activateTrialFromSuccessfulCheckout,
  fetchSubscriptionSnapshot,
  getPlanPromptReason,
  getSubscriptionSnapshot,
} from '../services/subscription';
import '../styles/HomePage.css';

const CHANGE_PLAN_REASON: PlanPromptReason = 'change-plan';

type EventsState = 'idle' | 'loading' | 'ready' | 'error';

const formatEventDate = (date: string | null, locale: string): string | null => {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

export const HomePage: FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [subscriptionSnapshot, setSubscriptionSnapshot] = useState(() => getSubscriptionSnapshot());
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalReason, setPlanModalReason] = useState<PlanPromptReason>('missing-plan');
  const [boatSetup, setBoatSetup] = useState<BoatCreationSetup | null>(null);
  const [boatModalOpen, setBoatModalOpen] = useState(false);
  const [boatSetupError, setBoatSetupError] = useState('');
  const [isBoatCreating, setIsBoatCreating] = useState(false);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(true);
  const [events, setEvents] = useState<HomeEventSummary[]>([]);
  const [eventsState, setEventsState] = useState<EventsState>('idle');
  const [eventsReloadKey, setEventsReloadKey] = useState(0);

  const refreshSubscription = () => {
    const nextSnapshot = getSubscriptionSnapshot();
    setSubscriptionSnapshot(nextSnapshot);
    return nextSnapshot;
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/authUser');
  }, [navigate]);

  const loadBoatSetup = useCallback(async () => {
    try {
      const setup = await fetchMyBoatSetup();
      setBoatSetup(setup);
      setBoatSetupError('');
      setBoatModalOpen(setup.mustCreateBoat);
      return setup;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('boats.create.setupError', 'No se pudo comprobar la configuración del barco.');
      setBoatSetup(null);
      setBoatSetupError(message);
      setBoatModalOpen(true);
      return null;
    }
  }, [t]);

  useEffect(() => {
    let isMounted = true;

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('checkout') === 'success') {
      activateTrialFromSuccessfulCheckout();
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const loadSubscription = async () => {
      try {
        const promptReason = getPlanPromptReason();
        const snapshot = await fetchSubscriptionSnapshot().catch(() => refreshSubscription());
        if (!isMounted) return;

        setSubscriptionSnapshot(snapshot);

        if (promptReason === 'new-user' && !snapshot.isUsable) {
          setBoatModalOpen(false);
          setPlanModalReason('new-user');
          setPlanModalOpen(true);
          return;
        }

        if (!snapshot.isUsable) {
          setBoatModalOpen(false);
          setPlanModalReason(snapshot.status === 'expired' ? 'expired' : 'missing-plan');
          setPlanModalOpen(true);
          return;
        }

        await loadBoatSetup();
      } finally {
        if (isMounted) setIsWorkspaceLoading(false);
      }
    };

    void loadSubscription();

    return () => {
      isMounted = false;
    };
  }, [loadBoatSetup]);

  const activeBoatId = boatSetup?.existingBoat?.id ?? null;

  useEffect(() => {
    if (!activeBoatId || boatSetup?.mustCreateBoat) {
      setEvents([]);
      setEventsState('idle');
      return;
    }

    const controller = new AbortController();
    setEventsState('loading');

    void fetchHomeEvents(activeBoatId, controller.signal)
      .then((nextEvents) => {
        if (controller.signal.aborted) return null;
        setEvents(nextEvents);
        setEventsState('ready');
        return nextEvents;
      })
      .catch(() => {
        if (controller.signal.aborted) return null;
        setEvents([]);
        setEventsState('error');
        return null;
      });

    return () => controller.abort();
  }, [activeBoatId, boatSetup?.mustCreateBoat, eventsReloadKey]);

  const handleCreateBoat = async (boatName: string) => {
    setIsBoatCreating(true);
    setBoatSetupError('');
    try {
      const result = await createMyBoat(boatName);
      setBoatSetup(result.setup);
      setBoatModalOpen(result.setup.mustCreateBoat);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('boats.create.createError', 'No se pudo crear el barco.');
      setBoatSetupError(message);
    } finally {
      setIsBoatCreating(false);
    }
  };

  const latestEvent = events[0] ?? null;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const latestEventDate = useMemo(
    () => formatEventDate(latestEvent?.event_date ?? null, locale),
    [latestEvent?.event_date, locale],
  );
  const activeBoatName =
    boatSetup?.existingBoat?.name ??
    boatSetup?.planBoatName ??
    boatSetup?.boatTypeName ??
    t('home.boatUnknown', 'Boat pending');
  const activeBoatType =
    boatSetup?.existingBoat?.boat_type ??
    boatSetup?.boatTypeName ??
    t('home.boatTypePending', 'Type pending');

  const eventsStatusCopy = (() => {
    if (eventsState === 'loading') return t('home.sessionsLoading', 'Updating sessions');
    if (eventsState === 'error') return t('home.sessionsErrorShort', 'Sessions unavailable');
    if (eventsState === 'ready' && events.length > 0) {
      return t('home.sessionsAvailable', '{{count}} sessions available', { count: events.length });
    }
    return t('home.sessionsEmptyShort', 'Ready for the first session');
  })();

  const analysisActionLabel = latestEvent
    ? t('home.analysisCtaLatest', 'Continue to the analysis workspace')
    : t('home.analysisCtaEmpty', 'Add and analyse a session');

  return (
    <div className="HomePage">
      <main className="navigation-container" id="main-content">
        {isWorkspaceLoading ? (
          <section className="home-loading-panel" role="status" aria-live="polite">
            <span className="home-loading-mark" aria-hidden="true" />
            <div>
              <h1>{t('home.loadingTitle', 'Preparing your navigation table')}</h1>
              <p>{t('home.loadingBody', 'Checking your plan, boat and latest sailing data.')}</p>
            </div>
          </section>
        ) : (
          <div className="home-workspace">
            <header className="home-workspace-header">
              <div className="home-heading-copy">
                <h1>{t('home.pageTitle', 'Your sailing data, ready for the next decision')}</h1>
                <p>
                  {t(
                    'home.pageIntro',
                    'Review the current context and latest session, or open a focused workflow.',
                  )}
                </p>
              </div>
              <span className="home-readiness" role="status" aria-live="polite">
                <span aria-hidden="true" />
                {eventsStatusCopy}
              </span>
            </header>

            <section className="home-context" aria-labelledby="home-context-title">
              <div className="home-section-heading">
                <h2 id="home-context-title">{t('home.contextTitle', 'Current context')}</h2>
              </div>
              <dl className="home-context-strip">
                <div>
                  <dt>{t('home.activeBoat', 'Active boat')}</dt>
                  <dd>
                    <strong>{activeBoatName}</strong>
                    <span>{activeBoatType}</span>
                  </dd>
                </div>
                <div>
                  <dt>{t('home.latestSession', 'Latest session')}</dt>
                  <dd>
                    <strong>
                      {latestEvent?.event_name ?? t('home.latestSessionEmpty', 'No sessions yet')}
                    </strong>
                    <span>
                      {latestEventDate ??
                        t('home.latestSessionHint', 'Add sailing data from Race Data Analysis')}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>{t('home.dataStatus', 'Data status')}</dt>
                  <dd>
                    <strong>{eventsStatusCopy}</strong>
                    {eventsState === 'error' ? (
                      <button
                        type="button"
                        className="home-inline-action"
                        onClick={() => setEventsReloadKey((key) => key + 1)}
                      >
                        <IconRefresh aria-hidden="true" stroke={1.8} />
                        {t('home.retrySessions', 'Retry')}
                      </button>
                    ) : (
                      <span>
                        {t('home.dataStatusHint', 'SailTrack keeps this context tied to your boat')}
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="home-actions-section" aria-labelledby="home-actions-title">
              <div className="home-section-heading">
                <h2 id="home-actions-title">{t('home.actionsTitle', 'Work from this context')}</h2>
                <p>
                  {t(
                    'home.actionsHint',
                    'One primary route, with reports and aggregation close at hand.',
                  )}
                </p>
              </div>

              <div className="home-action-layout">
                <button
                  type="button"
                  className="home-action home-action--primary"
                  onClick={() => navigate('/race-insights')}
                  aria-label={`${t('home.analysisTitle', 'Race Data Analysis')} — ${analysisActionLabel}`}
                >
                  <span className="home-action-icon" aria-hidden="true">
                    <ChartRaceDataIcon focusable="false" />
                  </span>
                  <span className="home-action-copy">
                    <span className="home-action-title">
                      {t('home.analysisTitle', 'Race Data Analysis')}
                    </span>
                    <span className="home-action-description">
                      {t(
                        'home.analysisDesc',
                        "Deep dive into your boat's performance metrics and data trends.",
                      )}
                    </span>
                    <span className="home-action-cta">
                      {analysisActionLabel}
                      <IconArrowUpRight aria-hidden="true" stroke={1.8} />
                    </span>
                  </span>
                </button>

                <div className="home-secondary-actions">
                  <button
                    type="button"
                    className="home-action home-action--secondary"
                    onClick={() => navigate('/reports')}
                  >
                    <span className="home-action-icon" aria-hidden="true">
                      <ClipboardReportsIcon focusable="false" />
                    </span>
                    <span className="home-action-copy">
                      <span className="home-action-title">{t('reports.title')}</span>
                      <span className="home-action-description">
                        {t('home.reportsDesc', 'Create daily reports in minutes!')}
                      </span>
                    </span>
                    <IconArrowUpRight
                      className="home-action-arrow"
                      aria-hidden="true"
                      stroke={1.8}
                    />
                  </button>

                  <button
                    type="button"
                    className="home-action home-action--secondary"
                    onClick={() => navigate('/analytics')}
                  >
                    <span className="home-action-icon" aria-hidden="true">
                      <AnalyticsSearchIcon focusable="false" />
                    </span>
                    <span className="home-action-copy">
                      <span className="home-action-title">
                        {t('home.analytics', 'Aggregated data')}
                      </span>
                      <span className="home-action-description">
                        {t(
                          'home.analyticsDesc',
                          'Aggregate boats, events, and files into one view.',
                        )}
                      </span>
                    </span>
                    <IconArrowUpRight
                      className="home-action-arrow"
                      aria-hidden="true"
                      stroke={1.8}
                    />
                  </button>
                </div>
              </div>
            </section>

            <WeatherWidget />
          </div>
        )}
      </main>

      <PlanSelectionModal
        open={planModalOpen}
        reason={planModalReason}
        mode={
          planModalReason === CHANGE_PLAN_REASON &&
          subscriptionSnapshot.stripeSubscriptionId &&
          subscriptionSnapshot.isUsable
            ? 'change'
            : 'checkout'
        }
        allowClose={planModalReason === CHANGE_PLAN_REASON && subscriptionSnapshot.isUsable}
        onClose={() => setPlanModalOpen(false)}
        onLogout={handleLogout}
        onPlanChanged={(nextSnapshot) => {
          setSubscriptionSnapshot(nextSnapshot);
          if (nextSnapshot.isUsable) {
            setIsWorkspaceLoading(true);
            void loadBoatSetup().finally(() => setIsWorkspaceLoading(false));
          }
        }}
      />
      <BoatCreationModal
        open={!planModalOpen && boatModalOpen}
        setup={boatSetup}
        isSubmitting={isBoatCreating}
        error={boatSetupError}
        onCreate={handleCreateBoat}
      />
    </div>
  );
};
